import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { Box, Button, DialogContentText, Stack, Typography } from '@mui/material';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { CustomFilterV2, DialogDefaultV2, TableV2 } from '../../../components/v2';
import { tableV2StackedCellContentSx } from '../../../components/v2/tableV2Styles';
import * as actionsUsuarios from '../../../store/usuario/actions';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as Roles from '../../../constants/role';
import { masks } from '../../../helpers';
import Formulario from './Formulario';

const Usuarios = ({ exportUsuarios, authData }) => {
  const [open, setOpen] = useState(false);
  const [id, setId] = useState(null);
  const [localFilter, setLocalFilter] = useState({});
  const [reload, setReload] = useState(false);
  const [openExport, setOpenExport] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);

  const isAdmin = authData && (authData.role === Roles.ADMIN || authData.role === Roles.SUPER_ADMIN);
  const canAdd = process.env.REACT_APP_USUARIO_CAN_ADD.split(',').includes(authData.email);
  const canExport = process.env.REACT_APP_USUARIO_EXPORT_REPORT.split(',').includes(authData.email);

  const apiFilter = useMemo(() => {
    const { ativo, ...rest } = localFilter;
    const mapped = { ...rest };
    if (ativo !== undefined && ativo !== null) mapped.usuarioAtivo = ativo;
    if (!isAdmin) mapped.usuarioAtivo = true;
    return mapped;
  }, [localFilter, isAdmin]);

  const onEdit = (rowId) => {
    setId(rowId);
    setOpen(true);
  };

  const onAdd = () => {
    setId(null);
    setOpen(true);
  };

  const onClose = (isSave) => {
    if (isSave) setReload((current) => !current);
    setOpen(false);
    setId(null);
  };

  const headerActions = [];
  if (canAdd) {
    headerActions.push({
      label: 'Novo',
      onClick: onAdd,
      startIcon: <AddIcon />,
      variant: 'contained',
    });
  }
  if (canExport) {
    headerActions.push({
      label: 'Exportar',
      onClick: () => setOpenExport(true),
      startIcon: <FileDownloadOutlinedIcon />,
      variant: 'outlined',
    });
  }

  const columns = [
    {
      field: 'nome',
      headerName: 'Nome',
      flex: 1.2,
      minWidth: 200,
      renderCell: ({ row }) => (
        <Box sx={tableV2StackedCellContentSx}>
          <Typography variant="body2">
            {row.nome} - {row.matricula}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {masks.phoneMask(row.telefone)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Login',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'roleName',
      headerName: 'Perfil',
      flex: 0.8,
      minWidth: 140,
      renderCell: ({ row }) => (
        <Box sx={tableV2StackedCellContentSx}>
          <Typography variant="body2">{row.roleName}</Typography>
          {row.subRoleName && (
            <Typography variant="body2" color="text.secondary">
              {row.subRoleName}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'setorName',
      headerName: 'Setor',
      flex: 0.6,
      minWidth: 120,
    },
    {
      field: 'ativo',
      headerName: 'Ativo',
      flex: 0.3,
      minWidth: 80,
      valueGetter: (_, row) => (row.ativo ? 'Sim' : 'Não'),
    },
  ];

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Typography variant="h4">Usuários</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <TableV2
          useApi
          fetchUrl="usuarios/filtro"
          paginationUrl="/v2/usuarios"
          filterData={apiFilter}
          refreshKey={reload}
          columns={columns}
          rowHeight={56}
          headerActions={headerActions}
          onEdit={isAdmin ? onEdit : undefined}
          onView={!isAdmin ? onEdit : undefined}
          sortComparator={(a, b) => (a.nome || '').localeCompare(b.nome || '')}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          appliedFilter={localFilter}
          fields={[
            { field: 'useUsuarios', md: 4, xs: 12, label: 'Usuário', key: 'usuarioFuncaoId' },
            { field: 'useRole', md: 3, xs: 12 },
            { field: 'useSetor', md: 3, xs: 12 },
            { field: 'usePossuiCarteira', md: 2, xs: 6 },
            ...(isAdmin ? [{ field: 'useAtivo', useAtivoLabel: 'Ativo', md: 2, xs: 6 }] : []),
          ]}
          onFilter={(filterData) => {
            setLocalFilter(filterData);
            setOpenFilter(false);
          }}
        />
      </Stack>

      <Formulario
        id={id}
        open={open}
        onClose={onClose}
        readOnly={!isAdmin}
        isAdmin={isAdmin}
        userEmail={authData.email}
      />

      <DialogDefaultV2
        open={openExport}
        handlerCancel={() => setOpenExport(false)}
        handlerConfirm={() => exportUsuarios(apiFilter, () => setOpenExport(false))}
        title="Confirmação"
        confirmMessage="Sim"
        cancelMessage="Não"
      >
        <DialogContentText>Deseja baixar todas as informações filtradas?</DialogContentText>
      </DialogDefaultV2>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ exportUsuarios: actionsUsuarios.exportUsuarios }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Usuarios));
